export type FileParts = {
    fullPath: string;
    folder: string;
    nameWithoutExtension: string;
    name: string;
    extension: string;
    directoryFullPath: string;
};

export const extractFileParts = (filePath: string): FileParts => {
    let separator = '/';
    if (filePath.includes('\\') && (!filePath.includes('/') || filePath.indexOf('\\') < filePath.indexOf('/'))) {
        separator = '\\';
    }

    const parts = filePath.split(separator);
    const name = parts.pop() || '';
    const folder = parts.length > 0 ? parts[parts.length - 1] : '';
    const nameParts = name.split('.');
    const extension = nameParts.length > 1 ? nameParts.pop()! : '';
    const nameWithoutExtension = nameParts.join('.');
    const directoryFullPath = parts.slice(0, parts.length - 1).join(separator);

    return {
        folder,
        nameWithoutExtension,
        name,
        extension,
        fullPath: filePath,
        directoryFullPath,
    };
};

export const extractExtension = (filePath: string) => {
    return filePath.split('.').pop()!;
};

export const isFileLogicProProject = (fileParts: FileParts) => {
    return fileParts.extension === 'logicx' || fileParts.directoryFullPath.endsWith('.logicx');
};

export const isFileGarageBandProject = (fileParts: FileParts) => {
    return fileParts.extension === 'band' || fileParts.directoryFullPath.endsWith('.band');
};

export const isFileAbletonLiveSet = (fileParts: FileParts) => {
    return fileParts.extension === 'als';
};

export const isFileAbletonLiveProject = (fileParts: FileParts) => {
    if (!isFileAbletonLiveSet(fileParts)) {
        return false;
    }

    if (fileParts.folder !== `${fileParts.nameWithoutExtension} Project`) {
        return false;
    }

    return true;
};

export const isFileFLStudioProject = (fileParts: FileParts) => {
    return fileParts.extension === 'flp';
};

export const isFileReaperProject = (fileParts: FileParts) => {
    return fileParts.extension === 'rpp';
};

export const isFileCubaseProject = (fileParts: FileParts) => {
    return fileParts.extension === 'cpr' || fileParts.extension === 'npr';
};

export const isFileStudioOneProject = (fileParts: FileParts) => {
    return fileParts.extension === 'song';
};

export const isDawProjectFile = (fileName: string) => {
    const fileParts = extractFileParts(fileName);
    return (
        isFileAbletonLiveSet(fileParts) ||
        isFileGarageBandProject(fileParts) ||
        isFileLogicProProject(fileParts) ||
        isFileFLStudioProject(fileParts) ||
        isFileReaperProject(fileParts) ||
        isFileCubaseProject(fileParts) ||
        isFileStudioOneProject(fileParts)
    );
};

export const isFileAudio = (fileParts: FileParts) => {
    return audioFileExtensions.includes(fileParts.extension);
};

export const findBounce = (projectFileInfo: FileParts, allFiles: FileParts[]) => {
    const directory = projectFileInfo.directoryFullPath;
    const bounce = allFiles.find(f => {
        if (!isFileAudio(f)) {
            return false;
        }

        if (f.directoryFullPath !== directory) {
            return false;
        }

        if (f.nameWithoutExtension !== projectFileInfo.nameWithoutExtension) {
            return false;
        }

        return true;
    });

    return bounce;
};

export const findMatchingFilesByBaseName = (targetFile: FileParts, allFiles: FileParts[]) => {
    return allFiles.filter(f =>
        f.nameWithoutExtension === targetFile.nameWithoutExtension &&
        f.fullPath !== targetFile.fullPath
    );
};

export const findLauncherOrBounceFile = (fileName: string) => {
    let launcherFile: string | undefined;
    let bounceFile: string | undefined;

    // Check if target file is audio or DAW project
    if (isAudioFile(fileName)) {
        bounceFile = fileName;
    } else if (isDawProjectFile(fileName)) {
        launcherFile = fileName;
    }

    return { launcherFile, bounceFile };
};

interface FileForBounceDetection {
    name: string;
    relative_path?: string;
}

export const getBounceFilePriority = (
    projectName: string,
    fileName: string,
    relativePath?: string
): number => {
    if (!isAudioFile(fileName)) {
        return 0;
    }

    const fileNameWithoutExt = fileName.split('.').slice(0, -1).join('.');

    if (projectName.includes(fileNameWithoutExt)) {
        return 3;
    }

    if (relativePath && !relativePath.includes('/')) {
        return 2;
    }

    return 1;
};

export const findBounceFileWithPriority = <T extends FileForBounceDetection>(
    projectName: string,
    allFiles: T[]
): T | undefined => {
    // Priority 1: Audio file where project name contains the audio filename (without extension)
    let bounceFile = allFiles.find(f =>
        isAudioFile(f.name) &&
        projectName.includes(f.name.split('.').slice(0, -1).join('.'))
    );

    // Priority 2: Audio file at the top level of the project (no slash in relative_path)
    if (!bounceFile) {
        bounceFile = allFiles.find(f =>
            isAudioFile(f.name) &&
            f.relative_path && !f.relative_path.includes('/')
        );
    }

    // Priority 3: Any audio file (fallback)
    if (!bounceFile) {
        bounceFile = allFiles.find(f => isAudioFile(f.name));
    }

    return bounceFile;
};

// const fileExtensionsToIgnore = new Set([
//     'DS_Store',
//     'aup3-wal',
//     'aup3-shm',
//     'asd',
// ]);

const fileMatchesToIgnore = [
    '.MAKID',
    'DS_Store',
    '.DS_Store',
    'aup3-wal',
    'aup3-shm',
    '.asd',
    'Autosave',
    'Backup',
];

export const shouldIgnoreFile = (fileName: string) => {
    // const extension = extractExtension(fileName);

    // if (fileExtensionsToIgnore.has(extension)) {
    //     return true;
    // }

    for (const match of fileMatchesToIgnore) {
        if (fileName.includes(match)) {
            return true;
        }
    }

    return false;
};

export const audioFileExtensions = ['.mp3', '.wav', '.m4a', '.aiff', '.aif', '.pcm', '.flac', '.alac', '.wma', '.aac', '.ogg', '.caf'];

export const isAudioFile = (originalFileName: string) => {
    const fileName = originalFileName.toLowerCase();
    return audioFileExtensions.some(ext => fileName.endsWith(ext));
};

export const videoFileExtensions = ['.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv', '.webm'];

export const isVideoFile = (originalFileName: string) => {
    const fileName = originalFileName.toLowerCase();
    return videoFileExtensions.some(ext => fileName.endsWith(ext));
};

export const midiFileExtensions = ['.mid', '.midi'];

export const isMidiFile = (originalFileName: string) => {
    const fileName = originalFileName.toLowerCase();
    return midiFileExtensions.some(ext => fileName.endsWith(ext));
};

export const getVideoMimeTypeFromFileName = (originalFileName: string) => {
    const fileName = originalFileName.toLowerCase();

    if (fileName.endsWith('.mp4')) {
        return 'video/mp4';
    }

    if (fileName.endsWith('.avi')) {
        return 'video/avi';
    }

    if (fileName.endsWith('.mov')) {
        return 'video/mp4';
    }

    if (fileName.endsWith('.mkv')) {
        return 'video/x-matroska';
    }

    if (fileName.endsWith('.wmv')) {
        return 'video/x-ms-wmv';
    }

    if (fileName.endsWith('.flv')) {
        return 'video/x-flv';
    }

    if (fileName.endsWith('.webm')) {
        return 'video/webm';
    }

    return 'video/mp4';
};

export const getMimeTypeFromFileName = (originalFileName: string) => {
    const fileName = originalFileName.toLowerCase();

    const mimeTypeMap: Record<string, string> = {
        '.mid': 'audio/midi',
        '.midi': 'audio/midi',
        '.mp3': 'audio/mpeg',
        '.wav': 'audio/wav',
        '.ogg': 'audio/ogg',
        '.flac': 'audio/flac',
        '.m4a': 'audio/mp4',
    };

    for (const [extension, mimeType] of Object.entries(mimeTypeMap)) {
        if (fileName.endsWith(extension)) {
            return mimeType;
        }
    }

    if (isAudioFile(fileName)) {
        return 'audio/mpeg';
    }

    if (isVideoFile(fileName)) {
        return getVideoMimeTypeFromFileName(fileName);
    }

    return 'application/octet-stream';
};
